/**
 * Chart Data Transformation Service Types
 *
 * This module contains TypeScript interfaces and types for the Chart Data Transformation Service
 * that transforms multi-dimensional survey data into Chart.js compatible data structures
 * using a flexible dimension mapping approach.
 */

import type { ChartData, ChartOptions } from 'chart.js';

// Enums
export enum ChartType {
  BAR = 'bar',
  LINE = 'line',
}

export enum DimensionType {
  QUESTIONS = 'questions',
  STATISTICS = 'statistics',
  DATALINES = 'datalines',
}

export enum NullHandling {
  SKIP = 'skip',
  ZERO = 'zero',
  PLACEHOLDER = 'placeholder',
}

export enum SortType {
  DEFAULT = 'default',
  ALPHABETICAL = 'alphabetical',
  VALUE = 'value',
}

export enum SortDirection {
  ASC = 'asc',
  DESC = 'desc',
}

export enum KeyTransform {
  NONE = 'none',
  HUMANIZE = 'humanize',
  UPPERCASE = 'uppercase',
}

// Interfaces
export interface DatalineSelection {
  focal: boolean;
  children?: 'all' | 'none' | string[] | number[];
  ancestors?: boolean | 'all' | 'none' | string[] | number[];
  selected?: boolean | 'all' | 'none' | string[] | number[];
}

export interface Selections {
  questions: string[];
  statistics: string[];
  datalines: DatalineSelection;
}

export interface FallbackStrategy {
  showKeys?: boolean;
  missingLabel?: string;
  keyTransform?: KeyTransform;
}

export interface LabelEntry {
  label: string;
  label_en: string;
}

export interface DefaultLabels {
  statistics?: Record<string, LabelEntry>;
  questions?: Record<string, LabelEntry>;
  datalines?: Record<string, LabelEntry>;
}

export interface CustomLabels {
  questions?: Record<string, LabelEntry>;
  statistics?: Record<string, LabelEntry>;
  datalines?: Record<string, LabelEntry>;
}

export interface LabelProvider {
  defaultLabels?: DefaultLabels;
  customLabels?: CustomLabels;
  fallbackStrategy?: FallbackStrategy;
}

export interface Labels {
  // Simple label overrides (backward compatibility)
  statisticLabels?: Record<string, string>;
  datalineLabels?: Record<string, string>;

  // Universal label provider
  labelProvider?: LabelProvider;
}

export interface DataHandling {
  nullHandling?: NullHandling;
  placeholderValue?: string | number;
}

export interface Sorting {
  xAxisSort?: SortType;
  seriesSort?: SortType;
  sortDirection?: SortDirection;
  sortByStatistic?: string;
}

export interface ChartConfig {
  // Chart type
  chartType: ChartType;

  // Dimension mapping
  xAxisData: DimensionType;
  seriesData: DimensionType;

  // Data selections
  selections: Selections;

  // Optional configurations
  labels?: Labels;
  dataHandling?: DataHandling;
  sorting?: Sorting;
}

// Chart.js output interface
export interface ChartJsOutput {
  type: string;
  data: ChartData;
  options?: ChartOptions;
}
